import HoverState from '@engoo/react-hover-state';
import React from 'react';
import { DAILY_NEWS_CATEGORY_TAGS } from 'app/constants/DailyNewsCategoryTags';
import { DailyNewsCategoryRoute, MeetingLandingRoute } from 'app/constants/Routes';
import { useCss } from 'app/utils/css';
import UserSensitiveLink from 'app/components/auth/UserSensitiveLink';
import Tag from 'app/components/svgs/Tag';
import Copyright from 'app/components/footer/Copyright';
import { useI18n } from 'app/components/localization';
import Link from 'app/components/router/Link';
import { NumericBreakpoint } from 'app/constants/Breakpoints';
import { usePageBreakpoint } from 'app/components/layout/Breakpoint';
import { FontWeight } from 'app/components/presentation/text/styles';
import { List } from 'immutable';
import { useBrand } from '../OrganizationContext';
import { getBrandDomainLocation } from 'app/utils/brand';
import { tFunctionType } from 'app/services/localization';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { BookingType } from 'app/models/rtc/booking/constants';
import { useHistoryLocation } from '../router/History';
import { getFullUrl } from 'app/utils/routes';
import BaseAnchor from '../router/BaseAnchor';

export interface IFooterDescription {
  getText: (t: tFunctionType) => string;
  getLink?: (t: tFunctionType) => string;
}

interface IFooterLink {
  getName: (t: tFunctionType) => string;
  href: string;
}

interface IProps {
  footerDescription?: IFooterDescription;
  footerLinks?: List<IFooterLink>;
  showDailyNewsTags?: boolean;
}

const linkStyleBase = {
  color: 'inherit',
  textDecoration: 'none',
  ':active': { textDecoration: 'underline' },
};

const linkHoverStyles = {
  textDecoration: 'underline',
};

const linkStyle = {
  ...linkStyleBase,
  marginLeft: '0.6em',
  marginRight: '1em',
};

const descriptionLinkStyle = {
  ...linkStyleBase,
  fontWeight: FontWeight.Bold,
};

const defaultFooterLinks = List<IFooterLink>([]);
const getDefaultFooterDescription = (brand: string): IFooterDescription => ({
  getText: (t) => t('footer.brand.description', { brand }),
  getLink: (t) => t('footer.brand.description-link', { brand }),
});

const MarketFooter: React.FunctionComponent<IProps> = ({
  footerDescription,
  footerLinks,
  showDailyNewsTags = false,
}) => {
  const location = useHistoryLocation();
  const { t } = useI18n();
  const css = useCss();
  const breakpoint = usePageBreakpoint();
  const brand = useBrand();
  const bookingState = useBookingState();
  const isMeeting = bookingState?.bookingType === BookingType.Meeting;

  const {
    getText: getDescriptionText,
    getLink: getDescriptionLink,
  } = footerDescription || getDefaultFooterDescription(brand.shortName ?? brand.name);
  const marketFooterLinks = footerLinks || defaultFooterLinks;
  const Logo = brand.FooterLogoComponent;

  const tagGroups = [];

  if (breakpoint > NumericBreakpoint.Narrow && showDailyNewsTags) {
    const tags = DAILY_NEWS_CATEGORY_TAGS;
    const tagsMiddle = Math.floor(tags.size / 2);
    tagGroups.push(tags.slice(0, tagsMiddle), tags.slice(tagsMiddle));
  }

  const descriptionClassName = css('description', [
    {
      fontSize: '0.8em',
      lineHeight: '1.7em',
    },
    breakpoint >= NumericBreakpoint.Wide && {
      margin: '1em 4em 0 0',
    },
    breakpoint === NumericBreakpoint.Medium && {
      margin: '0.5em 2em 0 0',
    },
    breakpoint <= NumericBreakpoint.Narrow && {
      margin: '1em 0',
    },
  ]);
  const linkClassName = css('link', [
    { ...linkStyle },
    breakpoint <= NumericBreakpoint.Narrow && {
      marginLeft: '0',
    },
  ]);
  const linkHoveredClassName = css('linkHovered', [
    { ...linkStyle },
    { ...linkHoverStyles },
    breakpoint <= NumericBreakpoint.Narrow && {
      marginLeft: '0',
    },
  ]);
  const descriptionLinkClassName = css('descriptionLink', { ...descriptionLinkStyle });
  const descriptionLinkHoveredClassName = css('descriptionLinkHovered', {
    ...descriptionLinkStyle,
    ...linkHoverStyles,
  });
  const linksClassName = css('links', [
    {
      listStyle: 'none',
      // Force alignment of right (in case there is only one link container)
      ':only-child': {
        marginLeft: 'auto',
      },
      padding: '0',
    },
    breakpoint <= NumericBreakpoint.Narrow && {
      display: 'block',
      width: '100%',
      padding: '0',
    },
  ]);
  const metaClassName = css('meta', [
    {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      maxWidth: '1200px',
      padding: '30px 25px',
      margin: '0 auto',
      '@media print': {
        padding: '10px 20px',
      },
      flexDirection: 'row',
    },
    breakpoint <= NumericBreakpoint.Medium && {
      padding: '30px 0px',
    },
    breakpoint <= NumericBreakpoint.Narrow && {
      flexDirection: 'column',
    },
  ]);
  const containerClassName = css('container', [
    {
      backgroundColor: '#fff',
      fontSize: '16px',
    },
    breakpoint === NumericBreakpoint.Medium && {
      fontSize: '14px',
    },
  ]);
  const logoAndDescriptionContainerClassName = css('logoAndDescriptionContainer', [
    {
      paddingLeft: '10px',
      '@media print': {
        flexGrow: 1,
      },
      flex: '0 1 585px',
    },
    breakpoint <= NumericBreakpoint.Narrow && {
      flex: '0 1 auto',
      width: '100%',
      boxSizing: 'border-box',
      paddingRight: '10px',
    },
  ]);
  const linksContainerClassName = css('linksContainer', [
    {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      fontSize: '0.8em',
      '@media print': {
        display: 'none',
      },
      flex: '0 1 460px',
    },
    breakpoint <= NumericBreakpoint.Narrow && {
      flex: '0 1 auto',
      flexDirection: 'column',
      width: '100%',
      maxWidth: 'auto',
      boxSizing: 'border-box',
      paddingLeft: '10px',
    },
  ]);
  const presentedByClassName = css('presentedBy', {
    fontSize: '0.8em',
    fontWeight: 600,
    marginBottom: '10px',
  });
  const linkItemClassName = css('linkItem', {
    marginBottom: '0.3em',
    ':last-child': { marginBottom: '0' },
  });
  const logoClassName = css('logo', {
    width: 'auto',
    height: '25px',
    maxWidth: '160px',
  });

  return (
    <div className={containerClassName}>
      {brand.isFirstParty &&
        <div className={metaClassName}>
          <div className={logoAndDescriptionContainerClassName}>
            <div className={presentedByClassName}>
              {t('footer.presented-by')}
            </div>
            {isMeeting ? (
              <BaseAnchor
                href={getFullUrl({
                  ...location,
                  pathname: MeetingLandingRoute.getPathname(),
                  search: undefined,
                })}
              >
                <Logo className={logoClassName} />
              </BaseAnchor>
            ) : (
              <UserSensitiveLink>
                <Logo className={logoClassName} />
              </UserSensitiveLink>
            )}

            <div className={descriptionClassName}>
              {getDescriptionText(t)}
              {getDescriptionLink !== undefined &&
                <HoverState>{(isHovered) =>
                  <Link
                    to={brand.landingPageUrl ?? getBrandDomainLocation(brand.defaultDomain)}
                    className={isHovered ? descriptionLinkHoveredClassName : descriptionLinkClassName}
                  >
                    {getDescriptionLink(t)}
                  </Link>
                }</HoverState>
              }
            </div>
          </div>
          <div className={linksContainerClassName}>
            {tagGroups.map((tagColumn, index) =>
              <ul key={index} className={linksClassName}>
                {tagColumn.map(tag =>
                  <li key={tag} className={linkItemClassName}>
                    <Tag size="0.9em" />
                    <HoverState>{(isHovered) =>
                      <Link
                        className={isHovered ? linkHoveredClassName : linkClassName}
                        to={DailyNewsCategoryRoute.getPathname({ category: tag })}
                      >
                        {tag}
                      </Link>
                    }</HoverState>
                  </li>,
                )}
              </ul>)}

            <ul className={linksClassName}>
              {marketFooterLinks.map(({ href, getName }) =>
                <li key={href} className={linkItemClassName}>
                  <HoverState>{(isHovered) => {
                    const className = isHovered ? linkHoveredClassName : linkClassName;
                    const name = getName(t);
                    return (
                      <Link to={href} className={className}>{name}</Link>
                    );
                  }}</HoverState>
                </li>,
              )}
            </ul>
          </div>
        </div>
      }
      <Copyright />
    </div>
  );
};

export default MarketFooter;
